import type { ReactNode } from 'react';
import { Trash } from '@phosphor-icons/react';
import type { ShopifySetupAgain } from '@discovr/types';
import { shopifyCopy } from '@discovr/features/copy';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';

const copy = shopifyCopy.setupAgain;

function reasonLine(reason: ShopifySetupAgain['reason']): string | null {
  switch (reason) {
    case 'role':
    case 'purge_pending':
      return copy.reasons[reason];
    case 'not_connected':
      return copy.connectStore;
    case 'store_unavailable':
      return shopifyCopy.state.store_unavailable.body;
    case 'uninstalling':
      return shopifyCopy.lifecycle.uninstallingNoShop;
    case 'campaign_inactive':
      return copy.dialog.errors.CAMPAIGN_NOT_ACTIVE ?? null;
    case null:
      return copy.lookupFailed;
    default:
      return null;
  }
}

/** Why the set-up-again switch is unavailable, for its tooltip and description. */
export function setupAgainUnavailableReason(setupAgain?: ShopifySetupAgain): string | null {
  if (!setupAgain || setupAgain.available) return null;
  return reasonLine(setupAgain.reason);
}

/** The one rendering of a campaign whose Shopify data was deleted after an
 *  uninstall. Without `setupAgain` it is the plain notice; a brand surface
 *  passes it to say whether Shopify can be set up again, and an optional
 *  `action` (a link) beneath. */
export function ShopifyDataRemovedNotice({
  className,
  detail = shopifyCopy.state.removed.body,
  setupAgain,
  store,
  action,
  onConnectStore,
}: {
  className?: string;
  detail?: string;
  setupAgain?: ShopifySetupAgain;
  store?: string;
  action?: ReactNode;
  onConnectStore?: () => void;
}) {
  const offer = !!setupAgain?.available && !!store;
  const needsStore = setupAgain?.reason === 'not_connected' && !!onConnectStore;
  const extra = !setupAgain
    ? null
    : offer
      ? copy.available(store)
      : needsStore
        ? null
        : setupAgain.available
          ? null
          : reasonLine(setupAgain.reason);

  return (
    <div
      role="status"
      className={cn(
        'border-border bg-muted/50 flex items-start gap-2 rounded-md border p-4 text-body-sm-regular',
        className,
      )}
    >
      <Trash aria-hidden="true" className="text-muted-foreground mt-0.5 size-4 shrink-0" />
      <div>
        <p className="text-foreground font-medium">{shopifyCopy.state.removed.title}</p>
        <p className="text-muted-foreground mt-0.5">
          {extra ? `${detail} ${extra}` : detail}
          {needsStore && (
            <>
              {' '}
              <Button variant="link" className="h-auto p-0 text-body-sm-regular" onClick={onConnectStore}>
                {copy.connectStore}
              </Button>
            </>
          )}
        </p>
        {action && <div className="mt-3">{action}</div>}
      </div>
    </div>
  );
}
